/**
 * Email Templates API Client
 * Handles all API calls to the KNKT-email backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5014/api/v1';

/**
 * Generic API request handler
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Templates API
 */
export const templatesAPI = {
  /**
   * Get all templates
   * @param {object} params - Query parameters (category, search, page, limit)
   * @returns {Promise<{success: boolean, data: Array, pagination: object}>}
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/email-templates${queryString ? `?${queryString}` : ''}`;
    return apiRequest(endpoint);
  },

  /**
   * Get starter templates
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  getStarters: async () => {
    return apiRequest('/email-templates/starters');
  },

  /**
   * Get single template by ID
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getById: async (id) => {
    return apiRequest(`/email-templates/${id}`);
  },

  /**
   * Create new template
   * @param {object} data - Template data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  create: async (data) => {
    return apiRequest('/email-templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Update template
   * @param {string} id - Template ID
   * @param {object} data - Updated template data
   * @returns {Promise<{success: boolean, data: object}>}
   */
  update: async (id, data) => {
    return apiRequest(`/email-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete template
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean}>}
   */
  delete: async (id) => {
    return apiRequest(`/email-templates/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Duplicate template
   * @param {string} id - Template ID
   * @param {string} newName - New template name
   * @returns {Promise<{success: boolean, data: object}>}
   */
  duplicate: async (id, newName) => {
    return apiRequest(`/email-templates/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ newName }),
    });
  },

  /**
   * Publish template to AWS SES
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  publish: async (id) => {
    return apiRequest(`/email-templates/${id}/publish`, {
      method: 'POST',
    });
  },

  /**
   * Unpublish template from AWS SES
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean}>}
   */
  unpublish: async (id) => {
    return apiRequest(`/email-templates/${id}/unpublish`, {
      method: 'POST',
    });
  },

  /**
   * Preview template with sample data
   * @param {string} id - Template ID
   * @param {object} previewData - Sample data for preview
   * @returns {Promise<{success: boolean, html: string, text: string}>}
   */
  preview: async (id, previewData = {}) => {
    return apiRequest(`/email-templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify({ previewData }),
    });
  },

  /**
   * Render template with real campaign data
   * @param {string} id - Template ID
   * @param {object} data - Real template data
   * @returns {Promise<{success: boolean, html: string, text: string}>}
   */
  render: async (id, data) => {
    return apiRequest(`/email-templates/${id}/render`, {
      method: 'POST',
      body: JSON.stringify({ data }),
    });
  },

  /**
   * Sync template to AWS SES (force re-sync)
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean}>}
   */
  sync: async (id) => {
    return apiRequest(`/email-templates/${id}/sync`, {
      method: 'POST',
    });
  },

  /**
   * Send test email
   * @param {string} id - Template ID
   * @param {string} testEmail - Recipient email address
   * @param {object} testData - Sample data for test
   * @returns {Promise<{success: boolean, message: string}>}
   */
  test: async (id, testEmail, testData = {}) => {
    return apiRequest(`/email-templates/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ testEmail, testData }),
    });
  },

  /**
   * Get template stats
   * @param {string} id - Template ID
   * @returns {Promise<{success: boolean, data: object}>}
   */
  getStats: async (id) => {
    return apiRequest(`/email-templates/${id}/stats`);
  },
};

export default templatesAPI;
